import type { CustomStoreDesign } from '@bloom/shared';
import { dtpPath, patientHomePath } from '../../../lib/clinicSite';
import { ReactNode, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, ShoppingCart, X, ArrowRight, Store, Package, User, Repeat } from 'lucide-react';
import { useDtpCartStore } from '../../../store/useDtpCartStore';
import { useDtpAuthStore } from '../../../store/useDtpAuthStore';
import { PatientNavigation } from '../PatientNavigation';
import { resolveStorefrontTheme, type StorefrontTheme } from '../../../lib/storefrontTheme';
import { useStorefrontPageIdentity } from '../../../hooks/useStorefrontBranding';

export interface ClinicInfo {
  name: string;
  physicianId: string;
  state?: string;
  /** States this clinic can ship to (own licenses ∪ telemed coverage). */
  allowedStates?: string[];
  /** Patient-facing coverage line computed by the catalog from allowedStates. */
  coverage?: import("@bloom/shared").StoreCoverage | null;
  isVet?: boolean;
  physicianFirstName?: string;
  physicianLastName?: string;
  physicianFullName?: string;
  storefrontVariant: 'lightweight' | 'educational' | 'custom';
  featuredProductId?: string;
  entryFlow?: import("@bloom/shared").StoreEntryFlow;
  customDesign?: CustomStoreDesign | null;
  membershipEnabled?: boolean;
  theme?: StorefrontTheme | null;
}

interface Props {
  clinic: ClinicInfo;
  children: ReactNode;
}

/** Inject (and clean up) the Google Fonts stylesheet for the active theme. */
function useStorefrontFonts(href: string | null) {
  useEffect(() => {
    if (!href) return;
    const existing = document.head.querySelector(`link[data-store-fonts="${href}"]`);
    if (existing) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-store-fonts', href);
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [href]);
}

export function EducationalShell({ clinic, children, patientAccount = false }: Props & { patientAccount?: boolean }) {
  useStorefrontPageIdentity(clinic.name, clinic.theme);
  const theme = resolveStorefrontTheme(clinic.theme);
  useStorefrontFonts(theme.googleFontsHref);

  return (
    <div
      className="store-themed min-h-screen bg-[var(--store-bg)] text-[var(--store-text)] antialiased"
      style={{ ...theme.cssVars, fontFamily: 'var(--font-body)' } as React.CSSProperties}
    >
      <EducationalHeader clinic={clinic} />
      {patientAccount && <div className="max-w-[1100px] mx-auto px-5 md:px-8 pt-6"><PatientNavigation /></div>}
      {children}
      <EducationalFooter clinic={clinic} />
    </div>
  );
}

export function EducationalHeader({ clinic }: { clinic: ClinicInfo }) {
  const navigate = useNavigate();
  const itemCount = useDtpCartStore(s => s.items.length);
  const isAuthenticated = useDtpAuthStore(s => s.isAuthenticated);
  const patient = useDtpAuthStore(s => s.patient);
  const logout = useDtpAuthStore(s => s.logout);
  const [menuOpen, setMenuOpen] = useState(false);

  const initial = (clinic.name || 'C').trim().charAt(0).toUpperCase();
  const subtitle = clinic.coverage?.label || '';
  const background = resolveStorefrontTheme(clinic.theme).bg;
  const dark = /^#[0-9a-f]{6}$/i.test(background) && (parseInt(background.slice(1, 3), 16) * .299 + parseInt(background.slice(3, 5), 16) * .587 + parseInt(background.slice(5, 7), 16) * .114) < 128;
  const logo = dark ? clinic.theme?.darkLogoUrl || clinic.theme?.logoUrl : clinic.theme?.logoUrl;

  const closeMenu = () => setMenuOpen(false);
  const go = (path: string) => { closeMenu(); navigate(path); };

  return (
    <>
      <header className="bg-[var(--store-bg,#faf8f4)] border-b border-[var(--store-border,#e8e2d6)] relative z-30">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-4 md:py-5 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate(dtpPath(clinic.physicianId, `/learn`))}
            className="flex items-center gap-3 md:gap-4 min-w-0 text-left"
          >
            {logo ? <img src={logo} alt={clinic.name} className="h-11 w-[120px] md:w-[180px] object-contain object-left" /> : <div className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-[var(--store-accent,#2d4a3a)] flex items-center justify-center text-white font-serif text-lg font-medium flex-shrink-0">
              {initial}
            </div>}
            <div className="min-w-0">
              <div className="font-serif text-[15px] md:text-[17px] font-medium leading-tight truncate">{clinic.name}</div>
              {subtitle && <div className="text-[11px] md:text-xs text-[var(--store-muted,#8a8175)] mt-0.5 truncate">{subtitle}</div>}
            </div>
          </button>

          <div className="flex items-center gap-1 md:gap-6 flex-shrink-0">
            <nav className="hidden md:flex items-center gap-8 text-sm text-[var(--store-muted,#4a4238)]">
              <button onClick={() => navigate(dtpPath(clinic.physicianId, `/learn`))} className="hover:text-[var(--store-text,#1a1612)]">Shop</button>
              {clinic.membershipEnabled && (
                <button onClick={() => navigate(dtpPath(clinic.physicianId, `/membership`))} className="hover:text-[var(--store-text,#1a1612)]">Membership</button>
              )}
              {isAuthenticated && (
                <button onClick={() => navigate(patientHomePath(clinic.physicianId))} className="hover:text-[var(--store-text,#1a1612)]">Patient portal</button>
              )}
              {isAuthenticated && patient ? (
                <button onClick={() => logout()} className="hover:text-[var(--store-text,#1a1612)]">Sign out</button>
              ) : (
                <button onClick={() => navigate(dtpPath(clinic.physicianId, '/checkout?intent=portal'))} className="hover:text-[var(--store-text,#1a1612)]">Sign in</button>
              )}
            </nav>
            <button
              onClick={() => navigate(dtpPath(clinic.physicianId, `/checkout`))}
              className="relative w-11 h-11 flex items-center justify-center text-[var(--store-text,#1a1612)]"
              aria-label="Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute top-1 right-1 bg-[var(--store-accent,#2d4a3a)] text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {itemCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              className="md:hidden w-11 h-11 flex items-center justify-center text-[var(--store-text,#1a1612)] -mr-2"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-[var(--store-bg,#faf8f4)] flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className="flex items-center justify-between p-5 border-b border-[var(--store-border,#e8e2d6)]">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-[var(--store-accent,#2d4a3a)] flex items-center justify-center text-white font-serif font-medium flex-shrink-0">
                {initial}
              </div>
              <div className="font-serif text-[15px] font-medium truncate">{clinic.name}</div>
            </div>
            <button onClick={closeMenu} className="w-11 h-11 flex items-center justify-center -mr-2" aria-label="Close menu">
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 p-5 space-y-1 overflow-y-auto">
            <MenuLink onClick={() => go(dtpPath(clinic.physicianId, `/learn`))} icon={<Store className="w-5 h-5" />} label="Shop" />
            {clinic.membershipEnabled && (
              <MenuLink onClick={() => go(dtpPath(clinic.physicianId, `/membership`))} icon={<Repeat className="w-5 h-5" />} label="Membership" />
            )}
            <MenuLink onClick={() => go(dtpPath(clinic.physicianId, `/checkout`))} icon={<ShoppingCart className="w-5 h-5" />} label={`Cart${itemCount > 0 ? ` (${itemCount})` : ''}`} />
            {isAuthenticated ? (
              <>
                <MenuLink onClick={() => go(patientHomePath(clinic.physicianId))} icon={<Package className="w-5 h-5" />} label="Patient portal" />
                <MenuLink onClick={() => { logout(); closeMenu(); }} icon={<User className="w-5 h-5" />} label="Sign out" />
              </>
            ) : (
              <MenuLink onClick={() => go(dtpPath(clinic.physicianId, '/checkout?intent=portal'))} icon={<User className="w-5 h-5" />} label="Sign in" />
            )}
          </nav>
        </div>
      )}
    </>
  );
}

function MenuLink({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between py-4 text-xl font-serif border-b border-[var(--store-border,#e8e2d6)] last:border-0"
    >
      <span className="flex items-center gap-3">
        <span className="text-[var(--store-muted,#8a8175)]">{icon}</span>
        {label}
      </span>
      <ArrowRight className="w-5 h-5 text-[var(--store-muted,#8a8175)]" />
    </button>
  );
}

export function EducationalFooter({ clinic }: { clinic: ClinicInfo }) {
  const coverage = clinic.coverage;
  const isAuthenticated = useDtpAuthStore(s => s.isAuthenticated);
  return (
    <footer className="bg-[var(--store-text,#1a1612)] text-white">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12 md:py-16 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
        <div className="col-span-2">
          {clinic.theme?.darkLogoUrl && <img src={clinic.theme.darkLogoUrl} alt={clinic.name} className="h-14 w-[220px] object-contain object-left mb-4" />}
          <div className="font-serif text-lg md:text-xl font-medium">{clinic.name}</div>
          {coverage && (
            <div className="text-sm text-stone-400 mt-2">{coverage.label}</div>
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-3 md:mb-4">Store</div>
          <div className="space-y-2 text-sm text-stone-300">
            <FooterLink to={dtpPath(clinic.physicianId, '/learn')}>Shop</FooterLink>
            {clinic.membershipEnabled && <FooterLink to={dtpPath(clinic.physicianId, '/membership')}>Membership</FooterLink>}
            <FooterLink to={dtpPath(clinic.physicianId, '/checkout')}>Cart</FooterLink>
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-3 md:mb-4">Account</div>
          <div className="space-y-2 text-sm text-stone-300">
            {isAuthenticated ? (
              <FooterLink to={patientHomePath(clinic.physicianId)}>Patient portal</FooterLink>
            ) : (
              <FooterLink to={dtpPath(clinic.physicianId, '/checkout?intent=portal')}>Sign in</FooterLink>
            )}
          </div>
        </div>
      </div>
      <div className="border-t border-stone-800">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-5 md:py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs text-stone-500">
          <div>Powered by Bloom Health</div>
          <div>© {new Date().getFullYear()} {clinic.name}</div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }: { to: string; children: ReactNode }) {
  return <Link to={to} className="block hover:text-white">{children}</Link>;
}
